// frontend/src/components/ui/NotificationToast.tsx
import React, { useEffect, useState } from 'react';
import { cn } from '@/utils';

export interface NotificationToastProps {
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  onClose: () => void;
  duration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  title?: string;
  actions?: React.ReactNode;
  persistent?: boolean;
  showProgress?: boolean;
  size?: 'sm' | 'md' | 'lg';
  animate?: boolean;
}

export const NotificationToast: React.FC<NotificationToastProps> = ({
  type,
  message,
  onClose,
  duration = 5000,
  position = 'top-right',
  title,
  actions,
  persistent = false,
  showProgress = false,
  size = 'md',
  animate = true
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (persistent) return;

    const progressInterval = showProgress ? setInterval(() => {
      setProgress(prev => {
        const decrement = 100 / (duration / 100);
        return Math.max(0, prev - decrement);
      });
    }, 100) : undefined;

    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, animate ? 300 : 0);
    }, duration);

    return () => {
      clearTimeout(timer);
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [duration, onClose, persistent, showProgress, animate]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, animate ? 300 : 0);
  };

  const typeStyles = {
    success: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      text: 'text-green-700 dark:text-green-300',
      icon: 'text-green-400 dark:text-green-300',
      progress: 'bg-green-500'
    },
    error: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      text: 'text-red-700 dark:text-red-300',
      icon: 'text-red-400 dark:text-red-300',
      progress: 'bg-red-500'
    },
    warning: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      text: 'text-yellow-700 dark:text-yellow-300',
      icon: 'text-yellow-400 dark:text-yellow-300',
      progress: 'bg-yellow-500'
    },
    info: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      text: 'text-blue-700 dark:text-blue-300',
      icon: 'text-blue-400 dark:text-blue-300',
      progress: 'bg-blue-500'
    }
  };

  const positionStyles = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
  };

  const sizeStyles = {
    sm: {
      container: 'max-w-xs p-3',
      icon: 'w-4 h-4',
      text: 'text-sm',
      title: 'text-sm font-medium',
      close: 'w-4 h-4'
    },
    md: {
      container: 'max-w-sm p-4',
      icon: 'w-5 h-5',
      text: 'text-sm',
      title: 'text-base font-medium',
      close: 'w-5 h-5'
    },
    lg: {
      container: 'max-w-md p-5',
      icon: 'w-6 h-6',
      text: 'text-base',
      title: 'text-lg font-medium',
      close: 'w-6 h-6'
    }
  };

  const currentTypeStyle = typeStyles[type];
  const currentSizeStyle = sizeStyles[size];

  const getIcon = () => {
    const iconClass = cn(currentSizeStyle.icon, currentTypeStyle.icon);
    
    switch (type) {
      case 'success':
        return (
          <svg className={iconClass} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L8.23 10.97a.75.75 0 00-1.06 1.061l1.5 1.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className={iconClass} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className={iconClass} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
        );
      case 'info':
        return (
          <svg className={iconClass} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  return (
    <div
      className={cn(
        'fixed z-50 transition-all duration-300 ease-in-out',
        currentSizeStyle.container,
        positionStyles[position],
        animate && (isVisible ? 'translate-x-0 opacity-100' : 
          position.includes('right') ? 'translate-x-full opacity-0' : 
          position.includes('left') ? '-translate-x-full opacity-0' : 
          position.includes('top') ? '-translate-y-full opacity-0' : 
          'translate-y-full opacity-0'
        )
      )}
    >
      {/* Progress Bar */}
      {showProgress && !persistent && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700 rounded-t-md overflow-hidden">
          <div
            className={cn('h-full transition-all duration-100 ease-linear', currentTypeStyle.progress)}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      
      {/* Toast Content */}
      <div className={cn(
        'border rounded-md shadow-lg',
        currentTypeStyle.bg,
        currentTypeStyle.border,
        showProgress && !persistent ? 'rounded-t-none' : ''
      )}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          
          <div className="ml-3 flex-1 min-w-0">
            {title && (
              <h3 className={cn(currentSizeStyle.title, currentTypeStyle.text, 'mb-1 break-words')}>
                {title}
              </h3>
            )}
            <p className={cn(currentSizeStyle.text, currentTypeStyle.text, 'break-words')}>
              {message}
            </p>
            
            {actions && (
              <div className="mt-3 flex flex-wrap gap-2">
                {actions}
              </div>
            )}
          </div>
          
          <div className="ml-auto pl-3">
            <button
              onClick={handleClose}
              className={cn(
                'inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2',
                'transition-colors duration-200 touch-manipulation',
                // Touch-friendly minimum size on mobile
                'min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0',
                type === 'success' && 'text-green-400 hover:bg-green-100 focus:ring-green-600 focus:ring-offset-green-50 dark:hover:bg-green-900/40',
                type === 'error' && 'text-red-400 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50 dark:hover:bg-red-900/40',
                type === 'warning' && 'text-yellow-400 hover:bg-yellow-100 focus:ring-yellow-600 focus:ring-offset-yellow-50 dark:hover:bg-yellow-900/40',
                type === 'info' && 'text-blue-400 hover:bg-blue-100 focus:ring-blue-600 focus:ring-offset-blue-50 dark:hover:bg-blue-900/40'
              )}
            >
              <span className="sr-only">Dismiss</span>
              <svg className={currentSizeStyle.close} viewBox="0 0 20 20" fill="currentColor">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Toast Container for managing multiple toasts
export const ToastContainer: React.FC<{ 
  position?: NotificationToastProps['position'];
  className?: string;
}> = ({ 
  position = 'top-right', 
  className 
}) => {
  return (
    <div className={cn(
      'fixed z-50 flex flex-col gap-2',
      position === 'top-right' && 'top-4 right-4',
      position === 'top-left' && 'top-4 left-4',
      position === 'bottom-right' && 'bottom-4 right-4',
      position === 'bottom-left' && 'bottom-4 left-4',
      position === 'top-center' && 'top-4 left-1/2 transform -translate-x-1/2',
      position === 'bottom-center' && 'bottom-4 left-1/2 transform -translate-x-1/2',
      className
    )}>
      {/* Toasts will be rendered here by a toast manager */}
    </div>
  );
};